import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingRefurbishment } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-refurbishment-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ImageModule, ProgressBarModule, TagModule, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent],
  templateUrl: './warehousing-refurbishment-view.component.html'
})
export class WarehousingRefurbishmentViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  isLoading = false;
  refurbishment?: WarehousingRefurbishment;
  items: WarehousingItem[] = [];
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Refurbishment Details', 'Refurbishment', '/warehousing/refurbishment');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void { this.loadRefurbishment(); }

  loadRefurbishment(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({ refurbishments: this.warehousingService.getRefurbishments(), items: this.warehousingService.getItems() }).subscribe({
      next: response => {
        this.refurbishment = (response.refurbishments.data || []).find(row => row.id === id);
        this.items = response.items.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  itemName(id?: number): string {
    const item = this.items.find(row => row.id === id);
    return itemIdentityLabel(item);
  }

  supplierName(refurbishment?: WarehousingRefurbishment): string {
    if (!refurbishment) {
      return '-';
    }
    return refurbishment.supplierClientName || refurbishment.vendorName || 'Internal team';
  }

  evidenceUrls(refurbishment?: WarehousingRefurbishment): string[] {
    return `${refurbishment?.photoUrls || ''}`
      .split('\n')
      .map(row => row.trim())
      .filter(Boolean);
  }

  evidenceFileName(url: string): string {
    const clean = (url || '').split('?')[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
  }

  isImageUrl(url: string): boolean {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((url || '').split('?')[0]);
  }

  metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.refurbishment?.status || '-', tone: this.refurbishment?.status === 'COMPLETED' ? 'success' : 'warn' },
      { label: 'Source', value: this.refurbishment?.sourceType || '-' },
      { label: 'Estimated Cost', value: this.formatAmount(this.refurbishment?.estimatedCost), tone: 'info' },
      { label: 'Actual Cost', value: this.formatAmount(this.refurbishment?.actualCost), tone: 'info' }
    ];
  }

  statusSeverity = statusSeverity;

  private formatAmount(value?: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value || 0);
  }
}
